import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Permission } from '../models';

interface PermissionRequest {
  code: string;
  name: string;
  description?: string;
  module?: string;
}

interface PermissionResponse {
  id: number;
  code: string;
  name: string;
  description?: string;
  module?: string;
  createdAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private apiUrl = '/api/permissions';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHeaders(): HttpHeaders {
    return this.authService.getAuthHeaders();
  }

  getAllPermissions(): Observable<Permission[]> {
    return this.http.get<PermissionResponse[]>(this.apiUrl, {
      headers: this.getHeaders()
    }).pipe(
      map(responses => responses.map(r => this.mapToPermission(r)))
    );
  }

  getPermissionById(id: number): Observable<Permission> {
    return this.http.get<PermissionResponse>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    }).pipe(
      map(response => this.mapToPermission(response))
    );
  }

  getModules(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/modules`, {
      headers: this.getHeaders()
    });
  }

  createPermission(request: PermissionRequest): Observable<Permission> {
    return this.http.post<PermissionResponse>(this.apiUrl, request, {
      headers: this.getHeaders()
    }).pipe(
      map(response => this.mapToPermission(response))
    );
  }

  updatePermission(id: number, request: PermissionRequest): Observable<Permission> {
    return this.http.put<PermissionResponse>(`${this.apiUrl}/${id}`, request, {
      headers: this.getHeaders()
    }).pipe(
      map(response => this.mapToPermission(response))
    );
  }

  deletePermission(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    });
  }

  private mapToPermission(response: PermissionResponse): Permission {
    return {
      id: response.id,
      code: response.code,
      name: response.name,
      description: response.description,
      module: response.module,
      createdAt: new Date(response.createdAt)
    };
  }
}
